(() => {
  'use strict';

  if (window.__awPlatformInheritanceP0) return;
  window.__awPlatformInheritanceP0 = true;

  const normalize = (value = '') => String(value ?? '')
    .replace(/[₂²]/g, '2')
    .replace(/[₀⁰]/g, '0')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const productKey = (value = '') => String(value ?? '').split('/').filter(Boolean).pop() || '';
  const list = (value) => {
    if (Array.isArray(value)) return value.map(normalize).filter(Boolean);
    if (!value) return [];
    try {
      const parsed = JSON.parse(String(value));
      if (Array.isArray(parsed)) return parsed.map(normalize).filter(Boolean);
    } catch (_) {}
    return String(value).split(',').map(normalize).filter(Boolean);
  };

  const positiveState = (status = '') => {
    const value = normalize(status);
    if (value === 'compatible-sous-condition' || value === 'compatible-sous-conditions') return 'conditional';
    if (value === 'compatible-verifie' || value === 'compatible-verifiee' || value === 'compatible-verified') return 'verified';
    if (value === 'compatible') return 'compatible';
    return '';
  };

  const blockedState = (status = '') => {
    const value = normalize(status);
    return value === 'a-verifier'
      || value.includes('incompatible')
      || value.includes('non-compatible')
      || value.includes('bloqu')
      || value.includes('refus');
  };

  const proofComplete = (relation) => {
    const score = Number(relation?.score);
    return Number.isFinite(score)
      && score >= 90
      && Boolean(String(relation?.date || '').trim())
      && Boolean(String(relation?.proof || '').trim());
  };

  const readIndex = () => {
    const node = document.querySelector('[data-aw-platform-inheritance-index]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[AW Compatibility] Index héritage plateforme invalide.', error);
      return [];
    }
  };

  const context = { modelId: '', model: '', platform: '', replicaType: '' };

  const readStoredContext = () => {
    try {
      context.platform = normalize(localStorage.getItem('aw_selected_platform') || context.platform);
      context.replicaType = normalize(localStorage.getItem('aw_selected_replica_type') || context.replicaType);
    } catch (_) {}
  };

  const updateContext = (detail = {}) => {
    if (Object.prototype.hasOwnProperty.call(detail, 'modelId')) context.modelId = normalize(detail.modelId || '');
    if (Object.prototype.hasOwnProperty.call(detail, 'model')) context.model = normalize(detail.model || '');
    if (Object.prototype.hasOwnProperty.call(detail, 'platform')) context.platform = normalize(detail.platform || '');
    if (Object.prototype.hasOwnProperty.call(detail, 'replicaType')) context.replicaType = normalize(detail.replicaType || '');
    readStoredContext();
  };

  const currentModelMatches = (expectedValue) => {
    const expected = normalize(expectedValue || '');
    if (!expected) return false;
    if (context.modelId && expected === context.modelId) return true;
    if (context.model && (expected === context.model || expected.endsWith(`-${context.model}`))) return true;
    return false;
  };

  const modelMatches = (override) => currentModelMatches(override?.model || '');

  const resolveExact = (entry) => {
    const exact = (Array.isArray(entry?.exactOverrides) ? entry.exactOverrides : []).filter(modelMatches);
    if (!exact.length) return null;
    if (exact.some((relation) => blockedState(relation?.status))) {
      return { state: 'blocked', scope: 'exact', relation: exact.find((relation) => blockedState(relation?.status)) };
    }
    const strong = exact.filter((relation) => positiveState(relation?.status) && proofComplete(relation));
    if (!strong.length) return { state: 'review', scope: 'exact', relation: exact[0] };
    const conditional = strong.find((relation) => positiveState(relation.status) === 'conditional');
    const chosen = conditional || strong[0];
    return { state: positiveState(chosen.status), scope: 'exact', relation: chosen };
  };

  const resolveGeneric = (entry) => {
    if (normalize(entry?.platform || '') !== context.platform) return null;

    // P0 fail-closed: an inherited relation must carry an explicit QA model scope.
    // Same platform, same brand, same propulsion or blank exact model are never sufficient.
    const allowedModels = list(entry?.allowedModels);
    if (!allowedModels.length || !allowedModels.some(currentModelMatches)) return null;

    const allowedTypes = list(entry?.types);
    if (allowedTypes.length) {
      if (!context.replicaType || !allowedTypes.includes(context.replicaType)) return null;
    }

    const state = positiveState(entry?.status);
    if (!state || !proofComplete(entry)) return null;
    return { state, scope: 'platform', relation: entry };
  };

  const resolveEntry = (entry) => {
    if (!context.platform || normalize(entry?.platform || '') !== context.platform) return null;
    const exact = resolveExact(entry);
    if (exact) return exact;
    return resolveGeneric(entry);
  };

  const rankVerdict = (verdict) => {
    if (!verdict) return 0;
    if (verdict.scope === 'exact' && verdict.state === 'blocked') return 100;
    if (verdict.scope === 'exact' && verdict.state === 'conditional') return 95;
    if (verdict.scope === 'exact' && verdict.state === 'compatible') return 92;
    if (verdict.scope === 'exact' && verdict.state === 'verified') return 90;
    if (verdict.scope === 'exact' && verdict.state === 'review') return 85;
    if (verdict.scope === 'platform' && verdict.state === 'conditional') return 75;
    if (verdict.scope === 'platform' && verdict.state === 'compatible') return 72;
    if (verdict.scope === 'platform' && verdict.state === 'verified') return 70;
    return 0;
  };

  let index = [];
  let byProduct = new Map();

  const rebuildIndex = () => {
    index = readIndex();
    byProduct = new Map();
    index.forEach((entry) => {
      const key = productKey(entry?.productId || '');
      const allowedModels = list(entry?.allowedModels);
      if (!key || !allowedModels.length) return;
      if (!byProduct.has(key)) byProduct.set(key, []);
      byProduct.get(key).push(entry);
    });
  };

  const baseBadge = (card) => card.querySelector('.aw-compatibility-search__badges .is-compatible');

  const resetCard = (card) => {
    const badge = baseBadge(card);
    if (badge?.dataset.awInheritanceDecorated === 'true') {
      badge.textContent = badge.dataset.awInheritanceBase || 'Plateforme associée — à vérifier';
      delete badge.dataset.awInheritanceDecorated;
      delete badge.dataset.awInheritanceBase;
      badge.removeAttribute('title');
    }
    delete card.dataset.awInheritanceState;
    delete card.dataset.awInheritanceScope;
    delete card.dataset.awInheritanceRelation;
  };

  const decorateCard = (card) => {
    resetCard(card);
    const entries = byProduct.get(productKey(card.dataset.productId || '')) || [];
    if (!entries.length || !context.platform) return;

    const verdicts = entries.map(resolveEntry).filter(Boolean).sort((a, b) => rankVerdict(b) - rankVerdict(a));
    if (!verdicts.length) return;
    const verdict = verdicts[0];
    const badge = baseBadge(card);
    if (!badge) return;

    if (verdict.state === 'blocked' || verdict.state === 'review') {
      card.dataset.awInheritanceState = verdict.state;
      card.dataset.awInheritanceScope = 'exact';
      card.dataset.awInheritanceRelation = String(verdict.relation?.relationId || '');
      return;
    }

    badge.dataset.awInheritanceBase = badge.textContent || 'Plateforme associée — à vérifier';
    badge.dataset.awInheritanceDecorated = 'true';
    if (verdict.scope === 'exact') {
      if (verdict.state === 'conditional') badge.textContent = 'Compatible sous condition';
      else if (verdict.state === 'compatible') badge.textContent = 'Compatible';
      else badge.textContent = 'Compatible vérifié';
    } else {
      if (verdict.state === 'conditional') badge.textContent = 'Compatible sous condition — portée plateforme contrôlée';
      else if (verdict.state === 'compatible') badge.textContent = 'Compatible — portée plateforme contrôlée';
      else badge.textContent = 'Compatible vérifié — portée plateforme contrôlée';
    }
    const proof = String(verdict.relation?.proof || '').trim();
    if (proof) badge.title = proof;
    card.dataset.awInheritanceState = verdict.state;
    card.dataset.awInheritanceScope = verdict.scope;
    card.dataset.awInheritanceRelation = String(verdict.relation?.relationId || '');
  };

  const decorateRoot = (root) => {
    root?.querySelectorAll?.('[data-aw-products] [data-aw-moat-compat-product]').forEach(decorateCard);
  };

  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      document.querySelectorAll('[data-aw-compatibility-search]').forEach(decorateRoot);
    });
  };

  const observed = new WeakSet();
  const install = (scope = document) => {
    const roots = [];
    if (scope?.matches?.('[data-aw-compatibility-search]')) roots.push(scope);
    scope?.querySelectorAll?.('[data-aw-compatibility-search]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const grid = root.querySelector('[data-aw-products]');
      if (!grid || observed.has(grid)) return;
      observed.add(grid);
      new MutationObserver(schedule).observe(grid, { childList: true });
    });
    schedule();
  };

  rebuildIndex();
  readStoredContext();

  window.addEventListener('aw:compatibility-changed', (event) => {
    updateContext(event.detail || {});
    schedule();
  });
  window.addEventListener('aw:platform-changed', (event) => {
    updateContext(event.detail || {});
    schedule();
  });
  window.addEventListener('aw:compatibility-index-ready', () => {
    rebuildIndex();
    schedule();
  });

  document.addEventListener('shopify:section:load', (event) => install(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => install(), { once: true });
  else install();
})();
